import { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
  Navigate,
  useLocation,
} from "react-router-dom";
import {
  ThemeProvider,
  CssBaseline,
  CircularProgress,
  Box,
} from "@mui/material";
import themeMain from "../theme/themeMain";
import ProtectedLayout from "./ProtectedLayout";
import ScrollToTop from "../components/ScrollToTop/ScrollToTop";
import SectionNavbar from "../components/SectionNavbar/SectionNavbar";
import ErrorBoundary from "../components/ErrorBoundary/ErrorBoundary";
import NotFound from "../pages/NotFound/NotFound";

const Main = lazy(() => import("../pages/PRI/main"));
const FundsMain = lazy(() => import("../pages/ETF/fundsMain"));
const Screener = lazy(() => import("../pages/Screener/Screener"));
const ETFComparisonMain = lazy(() =>
  import("../pages/ETFComparisonTool/ETFComparisonMain")
);
const IndicesHubPage = lazy(() =>
  import("../pages/IndicesHubPage/IndicesHubPage")
);
const Contact = lazy(() => import("../components/Contact/Contact"));
const TermsOfService = lazy(() =>
  import("../components/TermsOfService/TermsOfService")
);
const PrivacyPolicy = lazy(() =>
  import("../components/PrivacyPolicy/PrivacyPolicy")
);
const SignIn = lazy(() => import("../components/SignIn/SignIn"));
const Signup = lazy(() => import("../components/SignUp/SignUp"));
const ETFHub = lazy(() => import("../pages/ETF-Hub/ETF-Hub"));
const WatchList = lazy(() => import("../pages/WatchList/WatchList"));
const AddPortfolio = lazy(() => import("../pages/Portfolio/AddPortfolio"));
const PortfolioList = lazy(() => import("../pages/Portfolio/PortfolioList"));
const EditPortfolio = lazy(() => import("../pages/Portfolio/EditPortfolio"));
const ForgotPassword = lazy(() =>
  import("../components/ForgotPassword/ForgotPassword")
);
const ResetPassword = lazy(() =>
  import("../components/ResetPassword/ResetPassword")
);
const StockFinder = lazy(() => import("../pages/StockFinder/Stock-Finder"));
const Report = lazy(() => import("../pages/Report/Report"));

function PageLoader() {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "80vh",
      }}
    >
      <CircularProgress size={36} />
    </Box>
  );
}

/**
 * Single shared layout for all main navigation pages.
 *
 * The SectionNavbar is rendered here — above the route swap zone — so it
 * stays mounted for every navigation between ETF Hub, Screener, Research,
 * Compare, Indices and Index Research.  The navbar never unmounts → no jump.
 *
 * isDashboard routes (/ and /screener) get a viewport-clamped container
 * (height: 100vh, overflow: hidden) so their tables scroll internally.
 * All other routes get a normal scrolling page.
 *
 * The content wrapper is keyed to pathname so the pageEnter CSS animation
 * fires on every route change, giving a smooth 0.22 s fade-in.
 */
function MainLayout() {
  const { pathname } = useLocation();
  const isDashboard =
    pathname === "/" ||
    pathname === "/screener" ||
    pathname.startsWith("/funds/etf/") ||
    pathname === "/indiceshub" ||
    pathname.startsWith("/indices/") ||
    pathname === "/stock-finder" ||
    pathname === "/report";

  return (
    <Box
      sx={{
        height: isDashboard ? "100dvh" : "auto",
        display: "flex",
        flexDirection: "column",
        overflow: isDashboard ? "hidden" : "visible",
      }}
    >
      <SectionNavbar header noBorder={isDashboard} />

      <Box
        key={pathname}
        sx={{
          flex: isDashboard ? 1 : "unset",
          minHeight: isDashboard ? 0 : "auto",
          display: "flex",
          flexDirection: "column",
          animation: "pageEnter 0.22s ease",
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}

function RouteErrorBoundary({ children }) {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>;
}

function App() {
  return (
    <Router>
      <ThemeProvider theme={themeMain}>
        <CssBaseline />
        <ScrollToTop />
        <RouteErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/signin" element={<SignIn />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* All main pages share one persistent SectionNavbar */}
              <Route element={<MainLayout />}>
                <Route path="/" element={<ETFHub />} />
                <Route path="/screener" element={<Screener />} />
                <Route path="/funds/etf/:indexName" element={<FundsMain />} />
                <Route
                  path="/funds/etf/compare/:etfPair"
                  element={<ETFComparisonMain />}
                />
                <Route path="/indiceshub" element={<IndicesHubPage />} />
                <Route path="/indices/:indexName" element={<Main />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/stock-finder" element={<StockFinder />} />
                <Route path="/report" element={<Report />} />
              </Route>

              {/* Protected routes */}
              <Route element={<ProtectedLayout />}>
                <Route path="/watchlist" element={<WatchList />} />
                <Route path="/portfolio" element={<AddPortfolio />} />
                <Route path="/portfoliolist" element={<PortfolioList />} />
                <Route path="/portfolio/edit" element={<EditPortfolio />} />
              </Route>

              {/* 404 */}
              <Route path="/404" element={<NotFound />} />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
          </Suspense>
        </RouteErrorBoundary>
      </ThemeProvider>
    </Router>
  );
}

export default App;
